import React, { useState, useEffect } from 'react';
import { 
  FiMail, 
  FiCalendar, 
  FiSettings, 
  FiEye, 
  FiEdit3, 
  FiTrash2,
  FiSearch,
  FiDownload,
  FiUserPlus
} from 'react-icons/fi';
import BulkActionsBar from './BulkActionsBar';
import AdvancedFilter from './AdvancedFilter';
import api from '../services/api';

export default function UserManagementTable({ 
  users = [], 
  loading = false,
  onView,
  onEdit,
  onDelete,
  onBulkAction,
  pagination,
  onPageChange,
  searchTerm,
  onSearchChange,
  filters = {},
  onFiltersChange
}) {
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [showRoleEditor, setShowRoleEditor] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);

  // Fetch available roles and permissions using correct endpoints
  useEffect(() => {
    const fetchRolesAndPermissions = async () => {
      try {
        const [rolesResponse, permissionsResponse] = await Promise.all([
          api.get('/admin/roles'),
          api.get('/auth/permissions')
        ]);
        
        // Handle the actual API response format
        setAvailableRoles(rolesResponse.data.roles || []);
        setAvailablePermissions(permissionsResponse.data.permissions || permissionsResponse.data || []);
      } catch (error) {
        console.error('Failed to fetch roles/permissions:', error);
      }
    };
    fetchRolesAndPermissions();
  }, []);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedUsers(new Set(users.map(user => user.user_id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.size === 0) return;
    
    await onBulkAction(action, Array.from(selectedUsers));
    setSelectedUsers(new Set());
  };

  // Enhanced role editing functionality with VIP/Gifted/Trial constraints
  const handleEditRoles = async (user) => {
    setEditingUser(user);
    setRoleLoading(true);
    
    try {
      // Fetch current user roles and permissions
      const [rolesResponse, permissionsResponse] = await Promise.all([
        api.get(`/admin/users/${user.user_id}/roles`),
        api.get(`/admin/users/${user.user_id}/permissions`).catch(() => ({ data: { permissions: [] } }))
      ]);
      
      // Handle the actual API response format
      setUserRoles(rolesResponse.data.roles?.map(r => r.role_name || r) || []);
      setUserPermissions(permissionsResponse.data.permissions || []);
      setShowRoleEditor(true);
    } catch (error) {
      console.error('Failed to fetch user roles:', error);
      setUserRoles(user.roles?.map(r => r.role_name || r) || []);
      setUserPermissions([]);
      setShowRoleEditor(true);
    } finally {
      setRoleLoading(false);
    }
  };

  // Role assignment logic: Regular users can only have one role
  // Enterprise/tenant users can have multiple roles
  
  const handleRoleToggle = (roleName) => {
    setUserRoles(prev => {
      // Check if user is enterprise/tenant (can have multiple roles)
      const isEnterpriseTenant = editingUser?.subscription_status === 'enterprise' || 
                                editingUser?.user_role?.includes('tenant') ||
                                prev.some(role => role.includes('tenant') || role.includes('enterprise'));
      
      if (isEnterpriseTenant) {
        // Enterprise users can have multiple roles - use toggle logic
        if (prev.includes(roleName)) {
          return prev.filter(role => role !== roleName);
        } else {
          return [...prev, roleName];
        }
      } else {
        // Regular users can only have one role - replace existing role
        if (prev.includes(roleName)) {
          // If clicking the same role, remove it (deselect)
          return prev.filter(role => role !== roleName);
        } else {
          // Replace all roles with the selected one
          return [roleName];
        }
      }
    });
  };

  const handlePermissionToggle = (permissionName) => {
    setUserPermissions(prev => {
      if (prev.includes(permissionName)) {
        return prev.filter(permission => permission !== permissionName);
      } else {
        return [...prev, permissionName];
      }
    });
  };

  const handleSaveRoles = async () => {
    if (!editingUser) return;
    
    try {
      // Save roles and permissions
      await Promise.all([
        api.put(`/admin/users/${editingUser.user_id}/roles`, {
          roles: userRoles
        }),
        api.put(`/admin/users/${editingUser.user_id}/permissions`, {
          permissions: userPermissions
        }).catch(() => {
          // Permission endpoint might not exist yet, ignore error
          console.log('Permissions endpoint not available');
        })
      ]);
      
      setShowRoleEditor(false);
      setEditingUser(null);
      setUserRoles([]);
      setUserPermissions([]);
      
      // Refresh the users list to show updated roles
      window.location.reload(); // Simple refresh - could be improved with state update
    } catch (error) {
      console.error('Failed to update user roles:', error);
      alert('Failed to update user roles: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStatusBadge = (user) => {
    if (!user.is_active) {
      return (
        <span style={{
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: '500',
          backgroundColor: '#FEE2E2',
          color: '#991B1B'
        }}>
          Suspended
        </span>
      );
    }

    if (user.subscription_status === 'active') {
      return (
        <span style={{
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: '500',
          backgroundColor: '#D1FAE5',
          color: '#065F46'
        }}>
          Premium
        </span>
      );
    }

    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: '500',
        backgroundColor: '#F3F4F6',
        color: '#374151'
      }}>
        Standard
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const colors = {
      'Admin': { bg: '#FEF3C7', color: '#92400E' },
      'SuperAdmin': { bg: '#FECACA', color: '#991B1B' },
      'PlatformAdmin': { bg: '#DBEAFE', color: '#1E40AF' },
      'VIP': { bg: '#E0E7FF', color: '#3730A3' },
      'Gifted': { bg: '#F3E8FF', color: '#7C3AED' },
      'Trial': { bg: '#FEF3C7', color: '#D97706' },
      'Standard': { bg: '#F3F4F6', color: '#374151' },
      'Enterprise': { bg: '#D1FAE5', color: '#065F46' }
    };

    const colorScheme = colors[role] || colors['Standard'];

    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: '500',
        backgroundColor: colorScheme.bg,
        color: colorScheme.color,
        marginRight: '0.25rem',
        marginBottom: '0.25rem',
        display: 'inline-block'
      }}>
        {role}
      </span>
    );
  };

  // Enhanced search with highlighting
  const highlightText = (text, searchTerm) => {
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={{ backgroundColor: '#FEF3C7', padding: '0.1rem' }}>
          {part}
        </mark>
      ) : part
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E2E8F0'
      }}>
        <div style={{ textAlign: 'center', color: '#6B7280' }}>
          Loading users...
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #E2E8F0',
        overflow: 'hidden'
      }}>
        {/* Header with Search and Actions */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h3 style={{ margin: 0, color: '#1F2937', fontSize: '1.25rem', fontWeight: '600' }}>
            Users ({pagination?.total || users.length})
          </h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', position: 'relative' }}>
            {/* Enhanced Search with Custom Search Service */}
            <div style={{ position: 'relative' }}>
              <FiSearch style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6B7280',
                fontSize: '16px'
              }} />
              <input
                type="text"
                placeholder="Smart search users, emails, roles..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                style={{
                  paddingLeft: '2.5rem',
                  padding: '0.75rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  width: '300px'
                }}
              />
              {searchTerm && (
                <div style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '0.75rem',
                  color: '#6B7280',
                  backgroundColor: '#F3F4F6',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px'
                }}>
                  {users.length} results
                </div>
              )}
            </div>

            {/* Advanced Filter */}
            <div style={{ position: 'relative' }}>
              <AdvancedFilter
                isOpen={showAdvancedFilter}
                onToggle={() => setShowAdvancedFilter(!showAdvancedFilter)}
                onApply={onFiltersChange}
                onClear={() => onFiltersChange({})}
                filters={filters}
                entityType="users"
              />
            </div>

            {/* Export Button */}
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                backgroundColor: '#F9FAFB',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '0.875rem',
                cursor: 'pointer',
                color: '#374151'
              }}
              onClick={() => {
                // Export functionality
                const csvContent = users.map(user => 
                  `${user.first_name} ${user.last_name},${user.email},${user.roles?.join(';') || user.user_role},${user.created_at}`
                ).join('\n');
                
                const blob = new Blob([`Name,Email,Roles,Created\n${csvContent}`], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <FiDownload size={16} />
              Export CSV
            </button>

            {/* Add User Button */}
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
              onClick={() => {
                // TODO: Open add user modal
                console.log('Add new user');
              }}
            >
              <FiUserPlus size={16} />
              Add User
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB' }}>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #E5E7EB',
                  fontWeight: '600',
                  color: '#374151',
                  width: '40px'
                }}>
                  <input
                    type="checkbox"
                    checked={selectedUsers.size === users.length && users.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #E5E7EB',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  User
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #E5E7EB',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Roles & Status
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #E5E7EB',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Registration
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'left', 
                  borderBottom: '1px solid #E5E7EB',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Last Active
                </th>
                <th style={{ 
                  padding: '1rem', 
                  textAlign: 'right', 
                  borderBottom: '1px solid #E5E7EB',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr 
                  key={user.user_id} 
                  style={{ 
                    borderBottom: '1px solid #F1F5F9',
                    backgroundColor: selectedUsers.has(user.user_id) ? '#FEF3C7' : 'white'
                  }}
                >
                  <td style={{ padding: '1rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.user_id)}
                      onChange={() => handleSelectUser(user.user_id)}
                    />
                  </td>
                  
                  <td style={{ padding: '1rem' }}>
                    <div>
                      <div style={{ fontWeight: '500', color: '#1F2937' }}>
                        {highlightText(`${user.first_name} ${user.last_name}`, searchTerm)}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                        <FiMail size={14} />
                        {highlightText(user.email, searchTerm)}
                      </div>
                      {user.organization_name && (
                        <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.25rem' }}>
                          {user.organization_name}
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {/* Current RBAC Roles */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {user.roles && user.roles.length > 0 ? (
                          user.roles.map(role => getRoleBadge(role))
                        ) : (
                          getRoleBadge(user.user_role || 'Standard')
                        )}
                      </div>
                      
                      {/* Status Badge */}
                      {getStatusBadge(user)}
                    </div>
                  </td>
                  
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FiCalendar size={14} style={{ color: '#6B7280' }} />
                      <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                      </span>
                    </div>
                    {user.trial_used === 0 && (
                      <div style={{ fontSize: '0.75rem', color: '#10B981', marginTop: '0.25rem' }}>
                        Trial Available
                      </div>
                    )}
                  </td>
                  
                  <td style={{ padding: '1rem' }}>
                    <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                      {user.last_active_at ? 
                        new Date(user.last_active_at).toLocaleDateString() : 
                        'Never'
                      }
                    </span>
                  </td>
                  
                  <td style={{ padding: '1rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      <button
                        onClick={() => onView(user.user_id)}
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#6B7280'
                        }}
                        title="View User"
                      >
                        <FiEye size={14} />
                      </button>
                      
                      <button
                        onClick={() => handleEditRoles(user)}
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #3B82F6',
                          borderRadius: '6px',
                          backgroundColor: '#EFF6FF',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#3B82F6'
                        }}
                        title="Edit User Roles"
                      >
                        <FiSettings size={14} />
                      </button>
                      
                      <button
                        onClick={() => onEdit(user)}
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#6B7280'
                        }}
                        title="Edit User Profile"
                      >
                        <FiEdit3 size={14} />
                      </button>
                      
                      <button
                        onClick={() => onDelete(user.user_id)}
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #FCA5A5',
                          borderRadius: '6px',
                          backgroundColor: '#FEF2F2',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#EF4444'
                        }}
                        title="Delete User"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </span>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  color: pagination.page === 1 ? '#9CA3AF' : '#374151'
                }}
              >
                Previous
              </button>
              
              <span style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                color: '#6B7280',
                display: 'flex',
                alignItems: 'center'
              }}>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              
              <button
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  color: pagination.page === pagination.totalPages ? '#9CA3AF' : '#374151'
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {users.length === 0 && (
          <div style={{
            padding: '4rem',
            textAlign: 'center',
            color: '#6B7280'
          }}>
            {searchTerm ? `No users found matching "${searchTerm}"` : 'No users found'}
          </div>
        )}
      </div>

      {/* Enhanced Role Editor Modal - Simplified for actual endpoints */}
      {showRoleEditor && editingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 'var(--z-modal-backdrop)'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            width: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem', color: '#1F2937' }}>
              Edit Roles for {editingUser.first_name} {editingUser.last_name}
            </h3>
            
            <p style={{ color: '#6B7280', marginBottom: '2rem' }}>
              Select platform roles to assign to this user. Roles can be combined as documented in platform-roles-permissions.md. Each role grants specific permissions.
            </p>
            
            {roleLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                Loading roles...
              </div>
            ) : (
              <div>
                {/* Roles Section */}
                <div style={{ marginBottom: '2rem' }}>
                  <h4 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', color: '#1F2937' }}>
                    Available Roles
                  </h4>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '1rem' 
                  }}>
                    {availableRoles.map(role => {
                      // All roles can be combined as per platform-roles-permissions.md
                      const isDisabled = false;
                      
                      return (
                        <label 
                          key={role.role_id || role.role_name} 
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '1rem',
                            border: userRoles.includes(role.role_name) ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                            borderRadius: '8px',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            backgroundColor: userRoles.includes(role.role_name) ? '#EFF6FF' : (isDisabled ? '#F9FAFB' : 'white'),
                            opacity: isDisabled ? 0.5 : 1
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={userRoles.includes(role.role_name)}
                            onChange={() => !isDisabled && handleRoleToggle(role.role_name)}
                            disabled={isDisabled}
                            style={{ margin: 0 }}
                          />
                          <div>
                            <div style={{ fontWeight: '500', color: '#1F2937', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {role.role_name}
                              {/* No exclusive role indicators - roles can be combined per docs */}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                              {role.description || 'No description'}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Permissions Section */}
                <div style={{ marginBottom: '2rem' }}>
                  <h4 style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', color: '#1F2937' }}>
                    Direct Permissions (Platform Admin Only)
                  </h4>
                  <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '1rem' }}>
                    As a platform admin, you can assign individual permissions directly to users. This bypasses role-based permissions.
                  </p>
                  {availablePermissions.length > 0 ? (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                      gap: '0.75rem',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '1rem'
                    }}>
                      {availablePermissions.map(permission => (
                        <label 
                          key={permission.permission_name || permission} 
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={userPermissions.includes(permission.permission_name || permission)}
                            onChange={() => handlePermissionToggle(permission.permission_name || permission)}
                            style={{ margin: 0 }}
                          />
                          <span style={{ color: '#374151' }}>
                            {permission.permission_name || permission}
                          </span>
                          {permission.description && (
                            <span style={{ fontSize: '0.75rem', color: '#9CA3AF', marginLeft: '0.25rem' }}>
                              ({permission.description})
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: '0.875rem', color: '#9CA3AF', fontStyle: 'italic' }}>
                      No permissions available to assign
                    </p>
                  )}
                </div>

                {/* Summary Section */}
                <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#374151' }}>
                    Selected Roles:
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem' }}>
                    {userRoles.length === 0 ? (
                      <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>No roles selected</span>
                    ) : (
                      userRoles.map(role => getRoleBadge(role))
                    )}
                  </div>
                  
                  <h4 style={{ margin: '1rem 0 0.5rem 0', fontSize: '0.875rem', color: '#374151' }}>
                    Selected Permissions:
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem' }}>
                    {userPermissions.length === 0 ? (
                      <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>No direct permissions assigned</span>
                    ) : (
                      userPermissions.map(permission => (
                        <span 
                          key={permission}
                          style={{
                            fontSize: '0.75rem',
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#DBEAFE',
                            color: '#1E40AF',
                            borderRadius: '4px',
                            fontWeight: '500'
                          }}
                        >
                          {permission}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button
                onClick={() => {
                  setShowRoleEditor(false);
                  setEditingUser(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRoles}
                disabled={roleLoading}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  cursor: roleLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                {roleLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedUsers.size}
        entityType="users"
        onAction={handleBulkAction}
        onClearSelection={() => setSelectedUsers(new Set())}
      />
    </div>
  );
}
